import { ActionRequestStatus } from '@billing/validation';
import { Tooltip, UnstyledButton } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconAlertTriangle, IconArrowUpRight } from '@tabler/icons-react';
import type { ActionRequest } from 'interfaces/actionRequest';
import type { ReactElement } from 'react';
import { compareDateDesc } from 'utils/dateUtils';
import { ClaimAlertMark } from './ClaimAlertMark';

interface ClaimActionBadgeProps {
  requests: ActionRequest[];
  isProvider: boolean;
  onOpen: () => void;
}

export const getLatestVisibleActionRequest = (
  requests: ActionRequest[]
): ActionRequest | undefined => {
  const supersededRequestIDs = new Set(
    requests.map((request) => request.parentRequestID).filter(Boolean)
  );
  return requests
    .filter(
      (request) =>
        !supersededRequestIDs.has(request.id) &&
        (request.status === ActionRequestStatus.REQUESTED ||
          request.status === ActionRequestStatus.SUBMITTED ||
          request.status === ActionRequestStatus.REJECTED)
    )
    .sort((left, right) => compareDateDesc(left.createdAt, right.createdAt))[0];
};

export const actionRequestNeedsResponse = (
  request: ActionRequest,
  isProvider: boolean
): boolean => isProvider !== (request.status === ActionRequestStatus.SUBMITTED);

export const ClaimActionBadge = ({
  requests,
  isProvider,
  onOpen,
}: ClaimActionBadgeProps): ReactElement | null => {
  const { translate } = useTranslation();
  const latestRequest = getLatestVisibleActionRequest(requests);

  if (!latestRequest) return null;

  const needsResponse = actionRequestNeedsResponse(latestRequest, isProvider);
  const color = needsResponse
    ? 'var(--mantine-color-red-filled)'
    : 'var(--mantine-color-dimmed)';
  const role = isProvider ? 'provider' : 'biller';
  const state = needsResponse ? 'respond' : 'sent';
  const label = translate(
    `pages:claims.actionBadge.${state}`,
    needsResponse ? 'Respond' : 'Sent'
  );

  return (
    <Tooltip
      label={`${label} — ${translate(
        `pages:claims.actionBadge.${role}.${state}`
      )}`}
      events={{ hover: true, focus: true, touch: true }}
      withArrow
      position="top"
    >
      <UnstyledButton
        type="button"
        aria-label={label}
        style={{
          boxSizing: 'border-box',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          flexShrink: 0,
          padding: 0,
          border: `1px solid ${color}`,
          borderRadius: '50%',
        }}
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        <ClaimAlertMark color={color}>
          {needsResponse ? (
            <IconAlertTriangle size={14} aria-hidden />
          ) : (
            <IconArrowUpRight size={14} stroke={1.5} aria-hidden />
          )}
        </ClaimAlertMark>
      </UnstyledButton>
    </Tooltip>
  );
};
